import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import { Minus, Plus, RefreshCw, RotateCcw } from "lucide-react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Sphere,
  Graticule,
  ZoomableGroup,
} from "react-simple-maps";
import { cn } from "@/lib/utils";

export type ReachGeoRow = { region: string; value: number };

type Props = {
  data: ReachGeoRow[];
  learners?: number;
  loading?: boolean;
  onRefresh?: () => void;
  className?: string;
};

const GEO_URL = "/geo/countries-110m.json";
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

function normalizeName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");
}

const ALIASES: Record<string, string> = {
  usa: "united states of america",
  us: "united states of america",
  "united states": "united states of america",
  "u s a": "united states of america",
  uk: "united kingdom",
  "great britain": "united kingdom",
  england: "united kingdom",
  uae: "united arab emirates",
  "south korea": "south korea",
  "korea republic of": "south korea",
  "korea south": "south korea",
  russia: "russia",
  "russian federation": "russia",
  tanzania: "tanzania",
  "united republic of tanzania": "tanzania",
  "czech republic": "czech republic",
  czechia: "czech republic",
  "ivory coast": "cote divoire",
  "cote d ivoire": "cote divoire",
  "côte d'ivoire": "cote divoire",
  "dr congo": "democratic republic of the congo",
  drc: "democratic republic of the congo",
  congo: "republic of the congo",
  swaziland: "eswatini",
  "cape verde": "cabo verde",
  "east africa": "kenya",
  "west africa": "nigeria",
  "southern africa": "south africa",
  "north africa": "egypt",
  "central africa": "democratic republic of the congo",
};

const COUNTRY_COORDS: Record<string, [number, number]> = {
  afghanistan: [65, 33],
  albania: [20, 41],
  algeria: [3, 28],
  angola: [18, -12],
  argentina: [-64, -34],
  australia: [134, -25],
  austria: [14, 47.5],
  bangladesh: [90, 24],
  belgium: [4.5, 50.5],
  benin: [2.3, 9.5],
  botswana: [24, -22],
  brazil: [-55, -10],
  "burkina faso": [-1.6, 12.3],
  burundi: [29.9, -3.4],
  cameroon: [12, 6],
  canada: [-106, 56],
  "cabo verde": [-24, 16],
  "cape verde": [-24, 16],
  "central african republic": [21, 7],
  chad: [19, 15],
  chile: [-71, -35],
  china: [105, 35],
  colombia: [-72, 4],
  "republic of the congo": [15, -1],
  congo: [15, -1],
  "costa rica": [-84, 10],
  "cote divoire": [-5, 8],
  "ivory coast": [-5, 8],
  croatia: [15.2, 45.1],
  cuba: [-80, 22],
  "czech republic": [15.5, 49.8],
  czechia: [15.5, 49.8],
  "democratic republic of the congo": [23, -3],
  "dr congo": [23, -3],
  drc: [23, -3],
  denmark: [10, 56],
  djibouti: [42.6, 11.8],
  ecuador: [-78.2, -1.8],
  egypt: [30, 26],
  eritrea: [39, 15],
  estonia: [26, 59],
  eswatini: [31.5, -26.5],
  ethiopia: [40, 9],
  finland: [26, 64],
  france: [2, 46],
  gabon: [11.6, -0.8],
  gambia: [-15.5, 13.5],
  germany: [10, 51],
  ghana: [-1.2, 8],
  greece: [22, 39],
  guinea: [-10, 11],
  "guinea bissau": [-15, 12],
  haiti: [-72, 19],
  honduras: [-86, 15],
  hungary: [19, 47],
  india: [78, 21],
  indonesia: [118, -2],
  iran: [53, 32],
  iraq: [44, 33],
  ireland: [-8, 53],
  israel: [35, 31.5],
  italy: [12.5, 42.5],
  jamaica: [-77.3, 18.1],
  japan: [138, 36],
  jordan: [36, 31],
  kenya: [38, 0.5],
  kuwait: [47.5, 29.3],
  latvia: [25, 57],
  lebanon: [35.9, 33.9],
  lesotho: [28.2, -29.5],
  liberia: [-9.4, 6.4],
  libya: [17, 27],
  lithuania: [24, 55.2],
  madagascar: [47, -20],
  malawi: [34.3, -13.3],
  malaysia: [102, 4],
  mali: [-4, 17],
  mauritania: [-10, 20],
  mauritius: [57.5, -20.2],
  mexico: [-102, 23],
  morocco: [-5, 32],
  mozambique: [35.3, -18.3],
  myanmar: [96, 21],
  namibia: [17, -22],
  nepal: [84, 28],
  netherlands: [5.3, 52.1],
  "new zealand": [174, -41],
  nicaragua: [-85, 13],
  niger: [8, 17],
  nigeria: [8, 9.1],
  norway: [10, 62],
  oman: [57, 21],
  pakistan: [70, 30],
  panama: [-80, 9],
  paraguay: [-58, -23],
  peru: [-76, -10],
  philippines: [122, 13],
  poland: [20, 52],
  portugal: [-8, 39.4],
  qatar: [51.2, 25.3],
  romania: [25, 46],
  russia: [100, 61],
  rwanda: [29.9, -1.9],
  "saudi arabia": [45, 24],
  senegal: [-14.5, 14.5],
  serbia: [21, 44],
  "sierra leone": [-11.8, 8.5],
  singapore: [103.8, 1.3],
  somalia: [47, 6],
  "south africa": [25, -29],
  "south korea": [128, 36],
  spain: [-4, 40],
  "sri lanka": [80.8, 7.9],
  sudan: [30, 15],
  "south sudan": [30, 7],
  sweden: [15, 62],
  switzerland: [8, 47],
  syria: [38, 35],
  tanzania: [35, -6.4],
  "united republic of tanzania": [35, -6.4],
  thailand: [101, 15],
  togo: [1.2, 8],
  tunisia: [9, 34],
  turkey: [35, 39],
  uganda: [32.3, 1.4],
  ukraine: [32, 49],
  "united arab emirates": [54, 24],
  uae: [54, 24],
  "united kingdom": [-2, 54],
  uk: [-2, 54],
  "united states of america": [-98, 39],
  "united states": [-98, 39],
  usa: [-98, 39],
  uruguay: [-56, -33],
  venezuela: [-66, 8],
  vietnam: [108, 16],
  yemen: [48, 15.5],
  zambia: [27.8, -13.1],
  zimbabwe: [29.9, -19],
};

function canonicalRegion(region: string) {
  const key = normalizeName(region);
  return ALIASES[key] ?? key;
}

function lookupCoords(region: string): [number, number] | null {
  const key = canonicalRegion(region);
  return COUNTRY_COORDS[key] ?? COUNTRY_COORDS[normalizeName(region)] ?? null;
}

function countryMatches(geoName: string, region: string) {
  const geo = normalizeName(geoName);
  const want = canonicalRegion(region);
  if (!geo || !want) return false;
  if (geo === want) return true;
  if (geo.includes(want) || want.includes(geo)) return true;
  return false;
}

function fillForIntensity(intensity: number) {
  // Soft → deep Skutopia green
  const t = Math.min(1, Math.max(0, intensity));
  const r = Math.round(125 - t * 125);
  const g = Math.round(178 - t * 91);
  const b = Math.round(155 - t * 95);
  return `rgb(${r}, ${g}, ${b})`;
}

function AnimatedStat({
  label,
  value,
  suffix,
  delay = 0,
}: {
  label: string;
  value: number;
  suffix: string;
  delay?: number;
}) {
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => Math.round(v).toLocaleString());
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const controls = animate(motionVal, value, {
      duration: 1.1,
      delay,
      ease: [0.22, 1, 0.36, 1],
    });
    const unsub = rounded.on("change", setDisplay);
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, delay, motionVal, rounded]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <p className="text-[13px] text-gray-500">{label}</p>
      <p className="text-[22px] font-semibold tracking-tight text-gray-900 leading-tight">
        <span>{display}</span>
        {suffix ? ` ${suffix}` : ""}
      </p>
    </motion.div>
  );
}

export function ReachWorldMap({
  data,
  learners = 0,
  loading = false,
  onRefresh,
  className,
}: Props) {
  const mapShellRef = useRef<HTMLDivElement>(null);
  const [hoverRegion, setHoverRegion] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    region: string;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  const [position, setPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: [12, 4],
    zoom: 1,
  });

  const ranked = useMemo(
    () => [...data].filter((row) => row.value > 0).sort((a, b) => b.value - a.value),
    [data]
  );

  const maxValue = useMemo(
    () => Math.max(1, ...ranked.map((row) => row.value)),
    [ranked]
  );

  const valueByCanonical = useMemo(() => {
    const map = new Map<string, { region: string; value: number }>();
    for (const row of ranked) {
      const key = canonicalRegion(row.region);
      const prev = map.get(key);
      if (prev) map.set(key, { region: prev.region, value: prev.value + row.value });
      else map.set(key, { region: row.region, value: row.value });
    }
    return map;
  }, [ranked]);

  const markers = useMemo(() => {
    return ranked
      .map((row) => {
        const coords = lookupCoords(row.region);
        if (!coords) return null;
        return { ...row, coordinates: coords };
      })
      .filter(Boolean) as Array<ReachGeoRow & { coordinates: [number, number] }>;
  }, [ranked]);

  const countriesReached = ranked.length;
  const usersReached = ranked.reduce((sum, row) => sum + row.value, 0);
  const focusRegion = hoverRegion || selectedRegion;

  const findValueForGeo = (geoName: string) => {
    for (const [key, row] of valueByCanonical) {
      if (countryMatches(geoName, key) || countryMatches(geoName, row.region)) {
        return row;
      }
    }
    return null;
  };

  const focusCountry = (region: string, zoomIn = true) => {
    setSelectedRegion((prev) => (prev === region ? null : region));
    const coords = lookupCoords(region);
    if (zoomIn && coords) {
      setPosition({
        coordinates: coords,
        zoom: Math.min(MAX_ZOOM, Math.max(2.1, position.zoom)),
      });
    }
  };

  const updateTooltipFromEvent = (
    region: string,
    value: number,
    event: React.MouseEvent | MouseEvent
  ) => {
    const shell = mapShellRef.current;
    if (!shell) return;
    const rect = shell.getBoundingClientRect();
    setTooltip({
      region,
      value,
      x: Math.min(rect.width - 16, Math.max(16, event.clientX - rect.left)),
      y: Math.min(rect.height - 16, Math.max(16, event.clientY - rect.top)),
    });
  };

  const clearHover = () => {
    setHoverRegion(null);
    setTooltip(null);
  };

  const handleZoomIn = () =>
    setPosition((p) => ({ ...p, zoom: Math.min(MAX_ZOOM, +(p.zoom + 0.4).toFixed(2)) }));
  const handleZoomOut = () =>
    setPosition((p) => ({ ...p, zoom: Math.max(MIN_ZOOM, +(p.zoom - 0.4).toFixed(2)) }));
  const handleReset = () => {
    setPosition({ coordinates: [12, 4], zoom: 1 });
    setSelectedRegion(null);
    clearHover();
  };

  return (
    <div
      className={cn(
        "grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-4 items-stretch",
        className
      )}
    >
      <style>{`
        @keyframes reachPulse {
          0% { r: 4; opacity: 0.65; }
          70% { r: 16; opacity: 0; }
          100% { r: 16; opacity: 0; }
        }
        .reach-pulse { animation: reachPulse 2.4s ease-out infinite; }
        .reach-map-drag { cursor: grab; }
        .reach-map-drag:active { cursor: grabbing; }
      `}</style>

      {/* Left rail */}
      <div className="flex flex-col gap-5 min-h-0">
        <div className="space-y-4">
          {loading && ranked.length === 0 ? (
            <>
              <div>
                <p className="text-[13px] text-gray-500">Campaign Reach</p>
                <p className="text-[22px] font-semibold text-gray-900">…</p>
              </div>
              <div>
                <p className="text-[13px] text-gray-500">User Reached</p>
                <p className="text-[22px] font-semibold text-gray-900">…</p>
              </div>
            </>
          ) : (
            <>
              <AnimatedStat
                label="Campaign Reach"
                value={countriesReached}
                suffix={countriesReached === 1 ? "country" : "countries"}
                delay={0.05}
              />
              <AnimatedStat
                label="User Reached"
                value={usersReached || learners}
                suffix={(usersReached || learners) === 1 ? "user" : "users"}
                delay={0.12}
              />
            </>
          )}
        </div>

        {ranked.length > 0 && (
          <div className="flex-1 min-h-0 rounded-xl border border-[#e3ebe6] bg-[#f8fbf9] p-3">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5f6f67]">
                Top countries
              </p>
              <p className="text-[11px] text-gray-400">Click to focus</p>
            </div>
            <ul className="space-y-1 max-h-[220px] overflow-y-auto pr-1">
              {ranked.map((row, index) => {
                const active = focusRegion === row.region;
                const share = Math.round((row.value / maxValue) * 100);
                return (
                  <li key={row.region}>
                    <button
                      type="button"
                      onMouseEnter={() => setHoverRegion(row.region)}
                      onMouseLeave={() => setHoverRegion(null)}
                      onClick={() => focusCountry(row.region)}
                      className={cn(
                        "w-full rounded-lg px-2.5 py-2 text-left transition-colors",
                        active
                          ? "bg-white shadow-sm ring-1 ring-[#00573C]/25"
                          : "hover:bg-white/80"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-xs font-medium text-gray-900 truncate">
                          <span className="text-gray-400 mr-1.5 tabular-nums">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {row.region}
                        </span>
                        <span className="text-xs font-semibold tabular-nums text-[#00573C]">
                          {row.value.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#e2ebe6] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-[#00573C]"
                          initial={{ width: 0 }}
                          animate={{ width: `${share}%` }}
                          transition={{ duration: 0.55, delay: index * 0.04 }}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#00573C] hover:text-[#044d35] transition-colors self-start"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Click to refresh
          </button>
        )}
      </div>

      {/* Map */}
      <div className="flex flex-col gap-2 min-w-0">
        <div
          ref={mapShellRef}
          className="relative min-h-[320px] lg:min-h-[400px] rounded-2xl overflow-hidden border border-[#dfe8e3] reach-map-drag"
          onMouseLeave={clearHover}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(0,87,60,0.10),transparent_55%),linear-gradient(165deg,#eef5f1_0%,#f7faf8_45%,#ffffff_100%)]" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.28]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(0,87,60,0.14) 1px, transparent 0)",
              backgroundSize: "22px 22px",
            }}
          />

          {/* Controls */}
          <div className="absolute top-3 right-3 z-20 flex flex-col gap-1.5">
            <button
              type="button"
              onClick={handleZoomIn}
              className="h-8 w-8 rounded-lg border border-[#d5e0da] bg-white/95 text-[#1f2328] shadow-sm hover:bg-white flex items-center justify-center"
              aria-label="Zoom in"
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleZoomOut}
              className="h-8 w-8 rounded-lg border border-[#d5e0da] bg-white/95 text-[#1f2328] shadow-sm hover:bg-white flex items-center justify-center"
              aria-label="Zoom out"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="h-8 w-8 rounded-lg border border-[#d5e0da] bg-white/95 text-[#1f2328] shadow-sm hover:bg-white flex items-center justify-center"
              aria-label="Reset map"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 z-20 rounded-lg border border-[#d5e0da] bg-white/95 px-3 py-2 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#65756d] mb-1.5">
              Reach intensity
            </p>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400">Low</span>
              <div
                className="h-2 w-24 rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, #7db29b 0%, #3d9470 45%, #00573C 100%)",
                }}
              />
              <span className="text-[10px] text-gray-400">High</span>
            </div>
          </div>

          <p className="absolute bottom-3 right-3 z-20 text-[10px] text-[#7a8a82] bg-white/80 rounded-md px-2 py-1 border border-[#e3ebe6]">
            Drag to pan · Scroll or buttons to zoom
          </p>

          {/* Hover tooltip */}
          <AnimatePresence>
            {tooltip && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 2, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-[120%]"
                style={{ left: tooltip.x, top: tooltip.y }}
              >
                <div className="rounded-xl bg-[#12261e] text-white px-3 py-2 shadow-xl min-w-[132px]">
                  <p className="text-[12px] font-semibold leading-tight">{tooltip.region}</p>
                  <p className="text-[11px] text-[#9fd0b8] mt-0.5 tabular-nums">
                    {tooltip.value.toLocaleString()}{" "}
                    {tooltip.value === 1 ? "user" : "users"}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {loading && ranked.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 z-10">
              Loading geographic data…
            </div>
          ) : ranked.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 z-10">
              No geographic data available yet
            </div>
          ) : (
            <motion.div
              className="relative z-[1] h-full min-h-[320px] lg:min-h-[400px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 180 }}
                width={900}
                height={460}
                style={{ width: "100%", height: "100%" }}
              >
                <defs>
                  <radialGradient id="reachOcean" cx="50%" cy="45%" r="65%">
                    <stop offset="0%" stopColor="#f3faf6" />
                    <stop offset="100%" stopColor="#e4efe9" />
                  </radialGradient>
                  <filter id="reachGlow" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="1.8" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <ZoomableGroup
                  zoom={position.zoom}
                  center={position.coordinates}
                  onMoveEnd={(pos) =>
                    setPosition({
                      coordinates: pos.coordinates as [number, number],
                      zoom: pos.zoom,
                    })
                  }
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  translateExtent={[
                    [-200, -120],
                    [1100, 580],
                  ]}
                >
                  <Sphere
                    id="reach-sphere"
                    fill="url(#reachOcean)"
                    stroke="#d5e3db"
                    strokeWidth={0.4}
                  />
                  <Graticule stroke="#c5d6cc" strokeWidth={0.3} strokeOpacity={0.28} />

                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const name = String(geo.properties?.name || "");
                        const match = findValueForGeo(name);
                        const active = Boolean(match);
                        const isFocus =
                          !!match &&
                          !!focusRegion &&
                          (focusRegion === match.region ||
                            countryMatches(name, focusRegion));
                        const dimmed = !!focusRegion && active && !isFocus;
                        const intensity = match ? match.value / maxValue : 0;

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            onMouseEnter={(event) => {
                              if (!match) return;
                              setHoverRegion(match.region);
                              updateTooltipFromEvent(match.region, match.value, event);
                            }}
                            onMouseMove={(event) => {
                              if (!match) return;
                              updateTooltipFromEvent(match.region, match.value, event);
                            }}
                            onMouseLeave={clearHover}
                            onClick={() => {
                              if (match) focusCountry(match.region, true);
                            }}
                            style={{
                              default: {
                                fill: active
                                  ? dimmed
                                    ? "#9bb8a9"
                                    : fillForIntensity(0.45 + intensity * 0.55)
                                  : "#d7e2dc",
                                stroke: isFocus ? "#00573C" : "#ffffff",
                                strokeWidth: isFocus ? 1.1 : active ? 0.75 : 0.5,
                                outline: "none",
                                opacity: focusRegion && !active ? 0.55 : 1,
                                transition: "fill 180ms ease, opacity 180ms ease, stroke-width 180ms ease",
                                filter: isFocus ? "url(#reachGlow)" : undefined,
                                cursor: active ? "pointer" : "grab",
                              },
                              hover: {
                                fill: active ? "#044d35" : "#c8d5ce",
                                stroke: active ? "#00573C" : "#ffffff",
                                strokeWidth: active ? 1 : 0.55,
                                outline: "none",
                                cursor: active ? "pointer" : "grab",
                              },
                              pressed: {
                                fill: active ? "#033d2b" : "#c8d5ce",
                                stroke: "#ffffff",
                                strokeWidth: 0.7,
                                outline: "none",
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>

                  {markers.map((row, index) => {
                    const isFocus = focusRegion === row.region;
                    return (
                      <Marker key={row.region} coordinates={row.coordinates}>
                        <g
                          onMouseEnter={(event) => {
                            setHoverRegion(row.region);
                            updateTooltipFromEvent(row.region, row.value, event as unknown as React.MouseEvent);
                          }}
                          onMouseLeave={clearHover}
                          onClick={() => focusCountry(row.region, true)}
                          style={{ cursor: "pointer" }}
                        >
                          {(isFocus || !focusRegion) && (
                            <circle
                              className="reach-pulse"
                              fill="#00573C"
                              style={{ animationDelay: `${index * 0.2}s` }}
                            />
                          )}
                          <circle
                            r={isFocus ? 5.5 : 4}
                            fill="#00573C"
                            stroke="#fff"
                            strokeWidth={1.5}
                            filter="url(#reachGlow)"
                          />
                        </g>
                      </Marker>
                    );
                  })}
                </ZoomableGroup>
              </ComposableMap>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
